import {
  PricingCalculateResponse,
  Product,
  type Quote,
  addDaysIso,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
  quoteReplyValues,
} from '@deizi/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { type FromQuickQuote } from '../orders/NewOrderPage';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/PageHead';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ApiClientError, api } from '../../lib/api';
import {
  useChannels,
  useFilaments,
  useKits,
  usePrinters,
  useSupplies,
  useTemplates,
} from '../../lib/catalog-api';
import { errorMessage } from '../../lib/messages';
import { parseCentavos } from '../../lib/numbers';
import { useSettings } from '../../lib/settings-api';
import { useSetupStatus } from '../../lib/setup-api';
import { useDebounced } from '../../lib/use-debounced';
import { useIsPhone } from '../../lib/use-media';
import { SetupChecklist } from '../setup/SetupChecklist';
import {
  type Draft,
  buildRequest,
  clearDraft,
  describe,
  draftFromVariant,
  emptyDraft,
  fileDraft,
  loadDraft,
  platesFromParse,
  profileFromDraft,
  quickQuoteChannels,
  saveDraft,
} from './draft';
import { CatalogDialog } from '../products/CatalogDialog';
import { FilePanel, platesSummary } from './FilePanel';
import { type Catalog, InputsPanel } from './InputsPanel';
import { ReplyDialog } from './ReplyDialog';
import {
  ChannelCards,
  ChannelTable,
  CostCard,
  LeadNote,
  leadDays,
  priceRange,
  useBacklog,
} from './Results';
import { SaveProductDialog } from './SaveProductDialog';
import { SaveQuoteDialog } from './SaveQuoteDialog';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

/** Orçamento rápido: sliced file (or grams and time) → cost and price per channel (boards QQ-*). */
export function QuickQuotePage() {
  const status = useSetupStatus();
  if (!status.data) return <div className="skel" style={{ height: 320 }} />;
  if (!status.data.readyToQuote) {
    return (
      <>
        <PageHead crumb="Vender" title="Orçamento rápido" />
        <div className="col" style={{ gap: 18, maxWidth: 560 }}>
          <SetupChecklist />
        </div>
      </>
    );
  }
  return <QuickQuote />;
}

function QuickQuote() {
  const phone = useIsPhone();
  const toast = useToast();
  const printers = usePrinters();
  const filaments = useFilaments();
  const kits = useKits();
  const supplies = useSupplies();
  const channelsQ = useChannels();
  const templates = useTemplates();
  const settings = useSettings();
  const backlog = useBacklog();

  const [stored, setDraft] = useState<Draft>(loadDraft);
  const update = (fn: (d: Draft) => Draft) =>
    setDraft((d) => fn(d.printerId || !firstPrinter ? d : { ...d, printerId: firstPrinter }));
  const [view, setView] = useState<'inputs' | 'result'>('inputs');
  const [params, setParams] = useSearchParams();
  // "Orçar com este produto" (/orcamento-rapido?produto=id): open the picker on its variants.
  const [dialog, setDialog] = useState<'save' | 'reply' | 'product' | 'pick' | null>(() =>
    params.get('produto') ? 'pick' : null,
  );
  const [pickProduct, setPickProduct] = useState<string | null>(() => params.get('produto'));
  const [picking, setPicking] = useState(false);
  const navigate = useNavigate();
  // "Abrir orçamento rápido com arquivo de exemplo" (setup wizard): /orcamento-rapido?exemplo=1.
  const [sample] = useState(() => params.has('exemplo'));
  useEffect(() => {
    if (params.get('produto') || params.has('exemplo')) setParams({}, { replace: true });
  }, [params, setParams]);
  const [saved, setSaved] = useState<Quote | null>(null);
  const [replyChannel, setReplyChannel] = useState<string | null>(null);

  useEffect(() => saveDraft(stored), [stored]);

  // Default printer: the first one (the file's suggestion replaces it on upload).
  const firstPrinter =
    printers.data?.data.find((p) => p.status !== 'out_of_service')?.id ??
    printers.data?.data[0]?.id;
  const draft = stored.printerId || !firstPrinter ? stored : { ...stored, printerId: firstPrinter };

  const catalog: Catalog = {
    printers: printers.data?.data ?? [],
    filaments: filaments.data?.data ?? [],
    kits: kits.data?.data ?? [],
    supplies: supplies.data?.data ?? [],
    settings: settings.data,
  };
  const channels = useMemo(() => quickQuoteChannels(channelsQ.data?.data ?? []), [channelsQ.data]);

  const built = buildRequest(draft, { ...catalog, channels });
  const requestJson = built.request ? JSON.stringify(built.request) : null;
  const debounced = useDebounced(requestJson, 300);
  const calc = useQuery({
    queryKey: ['pricing', debounced],
    queryFn: () =>
      api.post('/pricing/calculate', PricingCalculateResponse, JSON.parse(debounced ?? '{}')),
    enabled: debounced !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const result = requestJson ? calc.data : undefined;
  const hasData = draft.mode === 'manual' || draft.file !== null || draft.source !== null;

  const overrideFor = (channelId: string) => {
    const t = draft.overrides[channelId];
    const v = t ? parseCentavos(t) : null;
    return v !== null && v > 0 ? v : null;
  };
  const visibleChannels = channels.filter((c) => !draft.hidden.includes(c.id));
  const description = describe(draft);
  const thumbnailFileId =
    draft.mode === 'file'
      ? (draft.filePlates.find((p) => p.included && p.thumbnailFileId)?.thumbnailFileId ?? null)
      : null;

  const clear = () => {
    clearDraft();
    setDraft({ ...emptyDraft(), printerId: firstPrinter ?? null });
    setSaved(null);
    setView('inputs');
    toast('Orçamento limpo');
  };

  const filePanel = (
    <FilePanel
      sample={sample}
      file={draft.file}
      source={draft.source}
      plates={draft.filePlates}
      onManual={() => update((d) => ({ ...d, mode: 'manual' }))}
      onPickProduct={() => {
        setPickProduct(null);
        setDialog('pick');
      }}
      onClear={() => update((d) => ({ ...d, file: null, source: null, filePlates: [] }))}
      onLoaded={(file, parsed) => {
        const plates = platesFromParse(parsed);
        setSaved(null);
        update((d) => ({
          ...d,
          mode: 'file',
          file: fileDraft(file, parsed),
          source: null,
          filePlates: plates,
          printerId: parsed.suggestedPrinterId ?? d.printerId,
          overrides: {},
        }));
        const secs = parsed.plates.reduce((n, p) => n + (p.sliced ? (p.printSeconds ?? 0) : 0), 0);
        const grams = parsed.plates.reduce((n, p) => n + (p.sliced ? (p.weightGrams ?? 0) : 0), 0);
        toast(
          `${file.name} lido: ${Math.floor(secs / 3600)}h${String(Math.round((secs % 3600) / 60)).padStart(2, '0')} · ${Math.round(grams)} g · ${plates.length} ${plates.length === 1 ? 'placa' : 'placas'}`,
        );
      }}
    />
  );

  const calcError =
    calc.error && requestJson === debounced ? (
      <Alert tone="err">
        <div className="col" style={{ gap: 6 }}>
          <span>
            <b>Não deu para calcular.</b> {errorMessage(calc.error)}
          </span>
          {calc.error instanceof ApiClientError &&
          typeof calc.error.details === 'object' &&
          calc.error.details !== null &&
          'field' in calc.error.details ? (
            <Link to="/configuracao-inicial">Completar a configuração</Link>
          ) : null}
        </div>
      </Alert>
    ) : null;

  const savedBanner = saved ? (
    <div className="alert ok">
      <Icon name="check" />
      <div className="col grow" style={{ gap: 8 }}>
        <p>
          <b>Orçamento {saved.number} salvo</b> · {saved.customer?.name ?? 'sem cliente'} ·{' '}
          {saved.channel?.name} · {formatBRL(saved.totals.priceCentavos)} · válido até{' '}
          {formatIsoDate(saved.validUntil)}
        </p>
        <div className="row wrap" style={{ gap: 8 }}>
          <Link className="btn sm" to={`/orcamentos/${saved.id}`}>
            Abrir orçamento
          </Link>
          <button type="button" className="btn sm p" onClick={() => setDialog('reply')}>
            <Icon name="copy" size={16} />
            Copiar resposta
          </button>
          <button type="button" className="btn sm g" onClick={() => setSaved(null)}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const actions = (
    <div className={phone ? 'col' : 'row wrap'} style={{ gap: 10 }}>
      <button
        type="button"
        className={phone ? 'btn p block' : 'btn p'}
        onClick={() => setDialog('save')}
        disabled={!result}
      >
        Salvar como orçamento
      </button>
      <button
        type="button"
        className={phone ? 'btn block' : 'btn'}
        onClick={() => setDialog('reply')}
        disabled={!result}
      >
        <Icon name="copy" size={18} />
        Copiar resposta
      </button>
      <div
        style={
          phone
            ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }
            : { display: 'contents' }
        }
      >
        <button
          type="button"
          className={phone ? 'btn sm' : 'btn'}
          disabled={!result || !built.request}
          onClick={() => {
            if (!result || !built.request) return;
            const channelId = visibleChannels[0]?.id ?? null;
            const fromQuickQuote: FromQuickQuote = {
              request: built.request,
              description: description || 'Peça',
              quantity: result.totals.quantity,
              unitPriceCentavos: channelId ? overrideFor(channelId) : null,
              channelId,
              slicedFileId: draft.mode === 'file' ? (draft.file?.id ?? null) : null,
              thumbnailFileId,
            };
            void navigate('/pedidos/novo', { state: { fromQuickQuote } });
          }}
        >
          Converter em pedido
        </button>
        <button
          type="button"
          className={phone ? 'btn sm' : 'btn g'}
          disabled={!result || !built.request || draft.source !== null}
          title={draft.source ? `Já é o produto ${draft.source.label}` : undefined}
          onClick={() => setDialog('product')}
        >
          Salvar como produto
        </button>
      </div>
    </div>
  );

  const resultsBody = result ? (
    <div className="col" style={{ gap: 16 }}>
      {savedBanner}
      <CostCard result={result} settings={settings.data} />
      {phone ? (
        <ChannelCards
          result={result}
          channels={channels}
          draft={draft}
          update={update}
          settings={settings.data}
        />
      ) : (
        <ChannelTable
          result={result}
          channels={channels}
          draft={draft}
          update={update}
          settings={settings.data}
        />
      )}
      <LeadNote result={result} settings={settings.data} />
      {actions}
    </div>
  ) : null;

  // Reply values before saving use the save channel (or the first visible one) and today's validity.
  const replyChannelId = saved?.channel?.id ?? replyChannel ?? visibleChannels[0]?.id ?? null;
  const replyEval = result?.channels.find((c) => c.channelId === replyChannelId)?.evaluation;
  const replyValues = saved
    ? quoteReplyValues({
        customerName: saved.customer?.name ?? null,
        items: saved.items,
        leadTimeDays: saved.leadTimeDays,
        validUntil: saved.validUntil,
      })
    : result
      ? quoteReplyValues({
          customerName: '[nome]',
          items: [
            {
              description: description || 'a peça',
              quantity: result.totals.quantity,
              unitPriceCentavos: replyEval?.priceCentavos ?? 0,
            },
          ],
          leadTimeDays: leadDays(result, settings.data, backlog),
          validUntil: addDaysIso(isoDateInSaoPaulo(), settings.data?.rules.quoteValidityDays ?? 7),
        })
      : null;

  const dialogs = (
    <>
      {dialog === 'save' && result && built.request ? (
        <SaveQuoteDialog
          request={built.request}
          result={result}
          channels={visibleChannels}
          settings={settings.data}
          description={description}
          defaultChannelId={replyChannelId}
          overrideFor={overrideFor}
          slicedFileId={draft.mode === 'file' ? (draft.file?.id ?? null) : null}
          thumbnailFileId={thumbnailFileId}
          variantId={draft.mode === 'file' ? (draft.source?.variantId ?? null) : null}
          onClose={() => setDialog(null)}
          onSaved={(q) => {
            setSaved(q);
            setDialog(null);
            toast(`Orçamento ${q.number} salvo`);
          }}
        />
      ) : null}
      {dialog === 'product' && built.request ? (
        <SaveProductDialog
          description={description}
          profile={profileFromDraft(draft, catalog.kits)}
          slicedFileId={draft.mode === 'file' ? (draft.file?.id ?? null) : null}
          summary={(() => {
            const sum = platesSummary(
              draft.mode === 'file' ? draft.filePlates : [draft.manualPlate],
            );
            return `${sum.time} · ${sum.grams} · ${sum.plates}`;
          })()}
          onClose={() => setDialog(null)}
          onSaved={(p) => {
            setDialog(null);
            toast(`Produto ${p.name} criado (${p.sku})`);
            void navigate(`/produtos/${p.id}/variacoes`);
          }}
        />
      ) : null}
      {dialog === 'pick' ? (
        <CatalogDialog
          title="Começar de um produto"
          intro={`Preenche placas, materiais, mão de obra e embalagem com o perfil de impressão da variação. Preço sugerido em ${visibleChannels[0]?.name ?? 'o primeiro canal'}.`}
          channelId={visibleChannels[0]?.id ?? null}
          priceLabel="Preço"
          actionLabel="Usar"
          productId={pickProduct}
          pending={picking}
          onClose={() => setDialog(null)}
          onPick={(item) => {
            setPicking(true);
            api
              .get(`/products/${item.product.id}`, Product)
              .then((product) => {
                const variant = product.variants.find((v) => v.id === item.variant.id);
                if (!variant) throw new Error('Variação não encontrada');
                setSaved(null);
                update((d) => draftFromVariant(d, product, variant));
                setDialog(null);
                toast(`${item.product.name} ${item.variant.name}: perfil carregado`);
              })
              .catch((err: unknown) => toast(errorMessage(err)))
              .finally(() => setPicking(false));
          }}
        />
      ) : null}
      {dialog === 'reply' && replyValues ? (
        <ReplyDialog
          key={`${replyChannelId}-${saved?.id ?? ''}`}
          templates={templates.data?.data ?? []}
          values={replyValues}
          whatsapp={saved?.customer?.whatsapp}
          marketplace={
            (channels.find((c) => c.id === replyChannelId)?.current?.commissionBps ?? 0) > 0
          }
          onClose={() => setDialog(null)}
          channelPicker={
            saved ? null : (
              <div className="field">
                <label htmlFor="rch">Canal</label>
                <Combobox
                  id="rch"
                  value={replyChannelId ?? ''}
                  options={visibleChannels.map(channelOption)}
                  onChange={setReplyChannel}
                />
              </div>
            )
          }
        />
      ) : null}
    </>
  );

  const missing = hasData && !requestJson ? built.missing : null;
  const emptyResult = (
    <div className="card">
      <div className="empty">
        <div className="emptyart" />
        <h2>Sem números ainda</h2>
        <p>
          {missing ??
            'Envie o G-code ou 3MF fatiado, ou digite gramas e tempo à mão. Os preços por canal aparecem aqui.'}
        </p>
        {hasData ? null : (
          <div className="row wrap" style={{ gap: 10, justifyContent: 'center' }}>
            <button
              type="button"
              className="btn"
              onClick={() => update((d) => ({ ...d, mode: 'manual' }))}
            >
              Digitar à mão
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (phone) {
    if (view === 'result' && result) {
      return (
        <div className="col" style={{ gap: 14 }}>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn icon g"
              aria-label="Voltar aos dados"
              onClick={() => setView('inputs')}
            >
              <Icon name="chevronsLeft" />
            </button>
            <div className="grow" style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22 }} className="trunc">
                {description || 'Orçamento'}
                {result.totals.quantity > 1 ? ` ×${result.totals.quantity}` : ''}
              </h1>
            </div>
          </div>
          {calcError}
          {resultsBody}
          {dialogs}
        </div>
      );
    }
    return (
      <div className="col" style={{ gap: 14 }}>
        {hasData ? (
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn sm g" onClick={clear}>
              Limpar
            </button>
          </div>
        ) : null}
        <InputsPanel
          draft={draft}
          update={update}
          catalog={catalog}
          result={result}
          hasData={hasData}
          filePanel={filePanel}
        />
        {calcError}
        {hasData ? (
          <div className="stickybar">
            {result ? (
              <>
                <div className="grow" style={{ minWidth: 0 }}>
                  <p className="xs muted">
                    Custo unitário {formatBRL(result.cost.unitCost)} · {visibleChannels.length}{' '}
                    {visibleChannels.length === 1 ? 'canal' : 'canais'}
                  </p>
                  <p className="fr tab" style={{ fontSize: 20, color: 'var(--pe)' }}>
                    {priceRange(result, channels, draft)}
                  </p>
                </div>
                <button type="button" className="btn p" onClick={() => setView('result')}>
                  Ver preços
                </button>
              </>
            ) : (
              <p className="sm muted grow">{missing ?? 'Calculando…'}</p>
            )}
          </div>
        ) : null}
        {dialogs}
      </div>
    );
  }

  return (
    <>
      <PageHead
        crumb="Vender"
        title="Orçamento rápido"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <button type="button" className="btn g" onClick={clear}>
              Limpar
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setPickProduct(null);
                setDialog('pick');
              }}
            >
              <Icon name="box" size={18} />
              Começar de um produto
            </button>
          </div>
        }
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <section
          className="col"
          style={{ flex: '0 1 340px', minWidth: 0, gap: 16 }}
          aria-label="Dados da peça"
        >
          <InputsPanel
            draft={draft}
            update={update}
            catalog={catalog}
            result={result}
            hasData={hasData}
            filePanel={filePanel}
          />
        </section>
        <section
          className="col"
          style={{ flex: '2 1 560px', minWidth: 0, gap: 16 }}
          aria-label="Resultado"
          aria-live="polite"
        >
          {calcError}
          {resultsBody ??
            (calc.isFetching && requestJson ? (
              <div className="skel" style={{ height: 320 }} />
            ) : (
              emptyResult
            ))}
        </section>
      </div>
      {dialogs}
    </>
  );
}
